import React, { useRef, useState } from 'react'

import { useBuyBonusStore, useConfigurationStore, useFreeBuyBonusStore, useFreeSpinsBonusesStore } from '@stores'

import declineIcon from '@assets/bonus-system/icon-cancel.svg'
import {
    Bonus,
    BonusesPlayLaterMessage,
    BonusesPlayNowMessage,
    BonusStates,
    BonusSystemState,
    BonusTypes,
    BuyBonusFeature,
    GameUIMessageType,
    HandleBonusByIdMessage,
} from 'game-ui-types'
import cssStyles from './BonusBundleTemplate.module.css'
import { useFormatMoney, useScaleToFit } from '@hooks'
import postponedClockIcon from '@assets/bonus-system/postponedClockIcon.svg'
import { useTranslation } from '@amusnet/translations/react'
import { cn, isFlexiBundle } from '@amusnet/react-ui'
import FSIcon from '@assets/bonus-system/FS_Icon.png'

export type BonusBundleStyles = {
    primaryTextColor: string
    secondaryTextColorBundle: string
    playBtnColor: string
    playLaterBtnColor: string
    declineBtnColor: string
    border: string
    background: string
}

interface BonusBundleTemplateProps {
    elementId: string
    bonus: Bonus
    bundleName?: string
    styles: BonusBundleStyles
    declinedBonusesIds: string[]
    setActiveBonus: (bonus?: Bonus) => void
    setSelectedBonus: (bonus: Bonus) => void
    setBonusSystemState: (state: string) => void
    setBonusSystemDeclineState: (state: string) => void
    setHasActivatedBonus?: (activated: boolean) => void
}

const BonusBundleTemplate: React.FC<BonusBundleTemplateProps> = ({
    elementId,
    bonus,
    bundleName,
    styles,
    declinedBonusesIds,
    setActiveBonus,
    setBonusSystemState,
    setHasActivatedBonus,
}: BonusBundleTemplateProps) => {
    const { t } = useTranslation()
    const formatMoney = useFormatMoney()
    const [isPostponeBoxVisible, setIsPostponeBoxVisible] = useState(false)
    const currency = useConfigurationStore((state) => state.currency)
    const hasBuyBonus = useConfigurationStore((state) => state.hasBuyBonus)
    const buyBonusConfig = useConfigurationStore((state) => state.buyBonusConfig)

    const setSelectedFeatureIndex = useBuyBonusStore((state) => state.setSelectedFeatureIndex)
    const setSelectedFeature = useBuyBonusStore((state) => state.setSelectedFeature)
    const updateCurrentFeatureMultiplier = useBuyBonusStore((state) => state.updateCurrentFeatureMultiplier)
    const setMultiChoiceToggle = useBuyBonusStore((state) => state.setMultiChoiceToggle)

    const bonusSystemState = useFreeBuyBonusStore((state) => state.bonusSystemState)
    const FBBonuses = useFreeBuyBonusStore((state) => state.bonuses)

    const FSBonusSystemState = useFreeSpinsBonusesStore((state) => state.bonusSystemState)
    const FSBonuses = useFreeSpinsBonusesStore((state) => state.bonuses)

    const betValue = isFlexiBundle(bonus)
        ? formatMoney(bonus.flexiBonusValue ?? 0)
        : formatMoney(Number(bonus.betValue))
    const betAmountRef = useRef<HTMLDivElement>(null)
    const bonusInfoContainerRef = useRef<HTMLDivElement>(null)
    const bonusTypeRef = useRef<HTMLDivElement>(null)
    const buttonContainerRef = useRef<HTMLDivElement>(null)
    const buttonRef = useRef<HTMLButtonElement>(null)
    const playLaterButtonRef = useRef<HTMLButtonElement>(null)
    const isInSingleBonusState =
        bonusSystemState === BonusSystemState.SINGLE_BONUS_INITIAL ||
        FSBonusSystemState === BonusSystemState.SINGLE_BONUS_INITIAL
    const isInMultipleBonusesState =
        bonusSystemState === BonusSystemState.MULTIPLE_BONUSES_INITIAL ||
        FSBonusSystemState === BonusSystemState.MULTIPLE_BONUSES_INITIAL
    const isFs = bonus.type === BonusTypes.FREE_SPIN

    const isBundleDeclined = !!declinedBonusesIds.find((id: string) => bonus.id === id)

    // TODO - check if we can reuse handlePlayNow from useBonusSystem hook, and we could also add handlePlayLater there too
    const handlePlayNow = () => {
        if (isFlexiBundle(bonus) && bonus.flexiBonusOptions && bonus.flexiBonusOptions?.length > 1) {
            const handleBonusByIdMessage: HandleBonusByIdMessage = {
                type: GameUIMessageType.HANDLE_BONUS_BY_ID,
                bonus,
            }

            window.postMessage(handleBonusByIdMessage)
        } else {
            const message: BonusesPlayNowMessage = {
                type: GameUIMessageType.BONUSES_PLAY_NOW,
                payload: { bonusIds: [bonus.id] },
            }

            window.postMessage(message)

            setHasActivatedBonus?.(true)
            setActiveBonus(bonus)
            setBonusSystemState(BonusSystemState.IN_PLAY)

            if (bonus.type !== BonusTypes.FREE_BUY_BONUS) {
                return
            }

            if (hasBuyBonus && buyBonusConfig.shouldTheFeatureStayActive) {
                const freeBuyBonusConfig = bonus?.typeConfiguration?.freeBuyBonusConfiguration

                const matchesActiveBonus = (feature: BuyBonusFeature) =>
                    feature.name === freeBuyBonusConfig?.featureId ||
                    feature.guaranteedName === freeBuyBonusConfig?.guaranteedName

                const selectedFeatureIndex = buyBonusConfig.features.findIndex(matchesActiveBonus)
                const currentFeature =
                    selectedFeatureIndex !== -1 ? buyBonusConfig.features[selectedFeatureIndex] : null

                setSelectedFeatureIndex(selectedFeatureIndex)
                setSelectedFeature(currentFeature)
                updateCurrentFeatureMultiplier(currentFeature?.multiplier ?? 1)
                setMultiChoiceToggle(true)
            }
        }
    }

    const renderPostponedBox = () => {
        return (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center font-bold text-[24px] text-white">
                <img
                    src={postponedClockIcon}
                    alt="Clock Icon"
                    className="absolute top-1/2 left-1/2 -translate-x-[350%] -translate-y-1/2"
                />

                <span>{t('gameui.postponed') ?? 'POSTPONED'}</span>
            </div>
        )
    }

    const handlePlayLater = () => {
        if (isInSingleBonusState && !bonus) {
            return
        }

        ;(isFs ? FSBonuses : FBBonuses).map((b) => {
            if (b.id === bonus.id) {
                b.state = BonusStates.CLAIMED
            }
        })

        const playedLaterBonusIndex = isFs ? FSBonuses.indexOf(bonus) : FBBonuses.indexOf(bonus)

        ;(isFs ? FSBonuses : FBBonuses).splice(playedLaterBonusIndex, 1)

        const filteredPayload = {
            bonusIds: (isFs ? FSBonuses : FBBonuses).map((b) => b.id).filter((id) => bonus.id === id),
        }

        if (isInSingleBonusState || isInMultipleBonusesState) {
            const isThereAnotherBonusToPlayLater = (isFs ? FSBonuses : FBBonuses).length > 0

            if (!isThereAnotherBonusToPlayLater) {
                const message: BonusesPlayLaterMessage = {
                    type: GameUIMessageType.BONUSES_PLAY_LATER,
                    payload: filteredPayload,
                }

                window.postMessage(message)
                setBonusSystemState(BonusSystemState.INACTIVE)
            }
        }

        setIsPostponeBoxVisible(!isPostponeBoxVisible)
    }

    const fsBundleType = bonus && t('gameui.bonusSystem.freeSpin', undefined, Number(bonus.remainingAmount))
    const bonusTypeText = isFs ? fsBundleType : bundleName
    const betAmountText = `x ${betValue} ${currency}`
    const scaleBonusInfo = useScaleToFit(bonusInfoContainerRef, bonusTypeRef, [bonusTypeText])
    const scaleBetAmount = useScaleToFit(bonusInfoContainerRef, betAmountRef, [betAmountText])
    const scaleButton = useScaleToFit(buttonContainerRef, buttonRef, [t('gameui.playNow')])
    const scalePlayLaterButton = useScaleToFit(buttonContainerRef, playLaterButtonRef, [t('gameui.playLater')])

    return (
        <div
            id={elementId}
            style={{ border: styles.border, background: styles.background }}
            className={cn(
                cssStyles.bonusBundleItem,
                'flex flex-row justify-between items-start h-[100px] w-[630px] mb-[8px] rounded-[8px] relative'
            )}
        >
            {isBundleDeclined && (
                <div
                    id="bundle-declined"
                    className="absolute inset-0 flex flex-row items-center justify-center bg-black/75"
                >
                    <img src={declineIcon} alt="Decline icon" className="w-8 h-8" />
                    <p className="text-3xl font-bold text-white pl-3">{t('gameui.declined')}</p>
                </div>
            )}

            {isFs && !!bonus.remainingAmount && (
                <div id="remaining-amount" className="w-fit text-left mx-4">
                    <p style={{ color: styles.primaryTextColor }} className="text-8xl font-bold leading-none pt-1">
                        {bonus.remainingAmount}
                    </p>
                </div>
            )}

            {isFs && !bonus.remainingAmount && bonus.flexiBonusValue && (
                <div id="flexi-bundle-img" className="mx-4">
                    <img src={FSIcon} className="w-fit" />
                </div>
            )}

            {isPostponeBoxVisible && renderPostponedBox()}

            <div
                ref={bonusInfoContainerRef}
                className={cn(
                    'text-left mr-4 flex flex-col grow items-start justify-center h-full whitespace-nowrap w-[239px]',
                    !isFs && 'pl-4'
                )}
            >
                <div
                    ref={bonusTypeRef}
                    className="text-left"
                    style={{ transform: `scale(${scaleBonusInfo})`, transformOrigin: 'left center' }}
                >
                    <p
                        id="bundle-type"
                        style={{ color: styles.primaryTextColor }}
                        className="text-3xl font-bold leading-none py-2"
                    >
                        {bonusTypeText}
                    </p>
                </div>
                {bonus.playLater && (
                    <button
                        id="play-later"
                        ref={playLaterButtonRef}
                        style={{ color: styles.playLaterBtnColor, zoom: scalePlayLaterButton }}
                        className="col-start-2 col-end-3 text-3xl leading-none font-bold border-none rounded cursor-pointer self-start mb-[15px]"
                        onClick={handlePlayLater}
                    >
                        {t('gameui.playLater')}
                    </button>
                )}
            </div>

            <div
                ref={buttonContainerRef}
                className="text-right mr-4 flex flex-col items-end justify-center h-full whitespace-nowrap w-[239px]"
            >
                <div
                    id="bonus-details"
                    ref={betAmountRef}
                    style={{ color: styles.secondaryTextColorBundle, zoom: scaleBetAmount }}
                    className="text-3xl text-white font-black leading-[30px] py-2"
                >
                    {betAmountText}
                </div>
                <button
                    id="play-now"
                    ref={buttonRef}
                    style={{ color: styles.playBtnColor, zoom: scaleButton }}
                    className="col-start-2 col-end-3 text-3xl leading-none font-bold border-none rounded cursor-pointer mb-[15px]"
                    onClick={handlePlayNow}
                    disabled={isBundleDeclined}
                >
                    {t('gameui.playNow')}
                </button>
            </div>
        </div>
    )
}

export default BonusBundleTemplate
